import React, { useEffect, useState } from "react";
import { Users, Target, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/** Compact dashboard widget for coaches:
 * shows total UE allocated across client sections + progress
 * + per-client breakdown so the coach knows exactly how much is left.
 */
export default function CoachAllocationsWidget({ coachUserId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const url = coachUserId ? `/coaches/${coachUserId}/allocations` : `/me/coach-allocations`;
        const res = await api.get(url);
        setData(res.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, [coachUserId]);

  if (loading) return null;
  if (!data || data.ue_target === 0) return null;

  const pct = data.progress_pct || 0;
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="coach-allocations-widget">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
            <Target size={11} strokeWidth={1.5} /> Meine Abschnitts-Zuweisungen
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Zugewiesene UE</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono text-slate-900">
            {data.ue_completed.toLocaleString("de-DE")} <span className="text-slate-400 text-base">/ {data.ue_target.toLocaleString("de-DE")} UE</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            <b className="text-slate-900">{data.ue_remaining.toLocaleString("de-DE")} UE übrig</b> · {pct}% erledigt
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-slate-100 relative overflow-hidden mb-4">
        <div className="absolute inset-y-0 left-0 bg-[#00CCC9]" style={{ width: `${pct}%` }} />
      </div>

      <div className="space-y-2">
        {data.clients.map((c) => {
          const cPct = c.ue_target ? Math.round(100 * c.ue_completed / c.ue_target) : 0;
          return (
            <Link key={c.client_id} to={`/avgs/${c.client_id}`}
              className="flex items-center gap-3 p-2 border border-slate-100 hover:bg-slate-50 hover:border-slate-300 transition-colors"
              data-testid={`alloc-client-${c.client_id}`}>
              <div className="w-6 h-6 bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                <Users size={10} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{c.client_name}</div>
                <div className="text-[10px] text-slate-500 font-mono truncate">
                  {c.sections.map((s) => s.name).join(" · ")}
                </div>
              </div>
              <div className="text-right text-[11px] font-mono">
                <div className="text-slate-900">{c.ue_completed}/{c.ue_target} UE</div>
                <div className="text-slate-500">{cPct}%</div>
              </div>
              <div className="w-20 h-1 bg-slate-100 relative overflow-hidden flex-shrink-0">
                <div className="absolute inset-y-0 left-0 bg-[#00CCC9]" style={{ width: `${cPct}%` }} />
              </div>
              <ChevronRight size={12} strokeWidth={1.5} className="text-slate-400 flex-shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
